import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, type AppDispatch } from '@/app/store/store';
import { selectFooterInViewport } from '@/app/store/footerVisibilitySlice';
import { browsingHistoryBuild } from '@/features/browsing-history/config/browsingHistory.build';
import { useBrowsingHistory } from '@/features/browsing-history/presentation/hooks/useBrowsingHistory';
import { RecentlyViewedListingShell } from '@/features/browsing-history/presentation/components/RecentlyViewedListingShell';
import { RecentlyViewedSeoHead } from '@/pages/product-listing/RecentlyViewedSeoHead';
import { RECENTLY_VIEWED_HEADER } from '@/features/browsing-history/infra/mappers/browsingHistory.mapper';
import { usePlpListing } from '@/features/plp/presentation/hooks/usePlpListing';
import { usePlpPincode } from '@/features/plp/presentation/hooks/usePlpPincode';
import { usePlpAnalytics } from '@/features/plp/presentation/hooks/usePlpAnalytics';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { PLP_DEFAULT_SORT } from '@/features/plp/constants';
import { PlpScrollToTopButton } from '@/shared/widgets/PlpScrollToTopButton';

const RECENTLY_VIEWED_PATH = '/recently-viewed';

export default function RecentlyViewedPlpPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { ref: topSentinelRef, isIntersecting: isTopVisible } = useIntersectionObserver<HTMLDivElement>();
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const footerInViewport = useAppSelector(selectFooterInViewport);
  const shouldPauseInfiniteScroll = useCallback(() => footerInViewport, [footerInViewport]);

  const { service } = useMemo(() => browsingHistoryBuild(dispatch), [dispatch]);
  const browsingHistory = useBrowsingHistory({ service, location, navigate });
  const { pincode } = usePlpPincode();

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    return () => {
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    };
  }, []);

  const plp = usePlpListing({
    fetchPage: browsingHistory.fetchPage,
    pathname: RECENTLY_VIEWED_PATH,
    listingKey: browsingHistory.listingKey,
    restoreKey: browsingHistory.listingKey,
    isMobile,
    shouldPauseInfiniteScroll,
  });

  const plpAnalytics = usePlpAnalytics({
    pageType: 'browse',
    listingHeader: RECENTLY_VIEWED_HEADER,
    collectionTags: '',
    currentSortBy: PLP_DEFAULT_SORT,
    totalCount: plp.listingMeta?.totalCount ?? 0,
  });

  const pageViewFiredRef = useRef<string | null>(null);
  useEffect(() => {
    plpAnalytics.viewedProductIds.clear();
  }, [browsingHistory.listingKey, plpAnalytics.viewedProductIds]);

  useEffect(() => {
    if (plp.isLoading) return;
    if (!plp.listingMeta) return;
    if (plp.allDesigns.length === 0) return;
    if (!plpAnalytics.isReady) return;
    if (pageViewFiredRef.current === browsingHistory.listingKey) return;
    pageViewFiredRef.current = browsingHistory.listingKey;
    plpAnalytics.trackPageView(plp.allDesigns);
    plpAnalytics.trackRecentlyViewedOpened(
      plp.allDesigns,
      plp.listingMeta?.totalCount ?? plp.allDesigns.length,
    );
  }, [plp.isLoading, plp.listingMeta, plp.allDesigns, browsingHistory.listingKey, plpAnalytics]);

  useEffect(() => {
    const handleScroll = () => plpAnalytics.trackScrollDown();
    window.addEventListener('scroll', handleScroll, { passive: true, once: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [plpAnalytics]);

  const showScrollToTop = isDesktop && !isTopVisible;

  return (
    <>
      <RecentlyViewedSeoHead />
      <div ref={topSentinelRef} aria-hidden />
      <RecentlyViewedListingShell
        {...plp}
        categories={browsingHistory.categories}
        activeCategory={browsingHistory.activeCategory}
        onCategoryClick={browsingHistory.onCategoryClick}
        pincode={pincode}
        openPdpInNewTab={isDesktop}
        onProductClick={plpAnalytics.trackProductClick}
        onWishlistClick={plpAnalytics.trackWishlistClick}
        onWishlistToggle={plpAnalytics.trackWishlistToggle}
        onVideoClick={plpAnalytics.trackVideoClick}
        onVideoCallCartAdd={plpAnalytics.trackVideoCallCartAdd}
        onViewDetailsClick={plpAnalytics.trackViewDetailsClick}
        onProductViewed={plpAnalytics.trackListingPageProductViewed}
        viewedProductIds={plpAnalytics.viewedProductIds}
        shouldPauseInfiniteScroll={shouldPauseInfiniteScroll}
      />
      <PlpScrollToTopButton show={showScrollToTop} />
    </>
  );
}
